import angular from 'angular';
import _ from 'lodash';
import './attachment.data.js';

(function () {
    'use strict';
    angular.module('app.core').factory('Invoice', InvoiceData);

    /* @ngInject */
    function InvoiceData($filter, $translate, logger, CONF, Attachment, UtilSvc) {
        Invoice.prototype = {
            build: build,
            getStatusLabel: getStatusLabel,
            getTypeLabel: getTypeLabel,
            isCreditNote: isCreditNote,
            getDownloadLink: getDownloadLink,
            getConsultLink: getConsultLink,
            getDematLabel: getDematLabel,
            hasAttachment: hasAttachment,
            hasOriginalFile: hasOriginalFile,
            displayLabelAndPerimeter: displayLabelAndPerimeter,
            getCommandOrContractRef: getCommandOrContractRef,
            isAdvancePayment: isAdvancePayment,
            isInvoice: isInvoice,
            getNbrAttachments: getNbrAttachments,
        };

        function Invoice(invoiceData) {
            if (invoiceData) {
                this.build(invoiceData);
            }
        }

        return Invoice;

        /////////////////////

        function build(invoiceData) {
            this.id = invoiceData._id || null;
            const functionalStatus = getFunctionalStatus(invoiceData);
            this.status = functionalStatus && functionalStatus.value;
            this.statusDate = functionalStatus && UtilSvc.parseDate(functionalStatus.date);
            this.creationDate = invoiceData.technical.doc_create_date || null;
            this.invoiceNumber = invoiceData.identifier || null;
            this.sellerName = invoiceData.seller.companyName || null;
            this.buyerName = invoiceData.buyer.companyName || null;
            this.buyerIdentifiant = invoiceData.buyer.taxIdentifier || null;
            this.transfertDate = UtilSvc.parseDate(invoiceData.otherWorkData.docSendingDate);
            this.dueDate = UtilSvc.parseDate(invoiceData.dueDate);
            this.valueDate = UtilSvc.parseDate(invoiceData.date);
            this.type = invoiceData.type;
            this.amountIncludingTaxes = _initNumber(invoiceData.total.amountIncludingTax);
            this.amountVat = _initNumber(invoiceData.total.vatAmount);
            this.currency = invoiceData.currency || null;
            this.attachmentItems = _.map(invoiceData.technical.contents, function (a) {
                return new Attachment(a);
            });
            this.demat = invoiceData.otherWorkData.senderDematType;
            this.orderRef = invoiceData.identifierBuyer.buyerOrder;
            this.attachement = 'attachement-off';
            this.consult = 'consult';
            this.download = 'download';
            this.perimeterName = invoiceData.perimeterName || null;
            this.invoiceReference = invoiceData.invoiceReference || null;
            this.otherExpensesAmount = _initNumber(invoiceData.total.otherExpenses);
            this.otherTaxAmount = _initNumber(invoiceData.total.taxAmount);
            this.sellerTransmissionChannel = invoiceData.otherWorkData.docSendingMode || null;
            this.pivotFile = invoiceData.pivotFile || false;
            this.perimeterCode = invoiceData.perimeterCode || null;
        }

        function _initNumber(value) {
            // Ne pas initialiser de la forme this.value = other || null
            // car si other vaut 0.0 alors sa valeur et fausse et on se retrouve avec null
            return value === undefined ? null : value;
        }

        /**
         * Récupère le label traduit du statut
         */
        function getStatusLabel() {
            if (this.status) {
                return $translate.instant('invoice.status.' + angular.lowercase(this.status));
            }
            return '';
        }

        /**
         * Récupère le label traduit du type
         */
        function getTypeLabel() {
            if (this.type) {
                return $translate.instant('invoice.type.' + angular.lowercase(this.type));
            }
            return '';
        }

        /**
         * Indique si la facture est un avoir ou non
         */
        function isCreditNote() {
            return this.type === '381';
        }

        function isInvoice() {
            return this.type === '380';
        }

        function isAdvancePayment() {
            return this.type === '386';
        }

        /**
         * Récupère le lien permettant de télécharger la facture
         */
        function getDownloadLink() {
            if (this.id) {
                return (
                    CONF.appContext +
                    CONF.apiBaseUrl +
                    CONF.apiVersion +
                    '/invoices/' +
                    this.id +
                    '/attachment/' +
                    _getInvoiceOrigGUID(this.attachmentItems) +
                    '?disposition=ATTACHMENT'
                );
            }
            return null;
        }

        /**
         * Récupère le lien de consultation d'une facture
         */
        function getConsultLink() {
            if (this.id) {
                return (
                    CONF.appContext +
                    CONF.apiBaseUrl +
                    CONF.apiVersion +
                    '/invoices/' +
                    this.id +
                    '/attachment/' +
                    _getInvoiceOrigGUID(this.attachmentItems) +
                    '?disposition=INLINE'
                );
            }
            return null;
        }

        /**
         * Récupère le label traduit du mode de demat
         */
        function getDematLabel() {
            if (this.demat) {
                return $translate.instant('invoices.demat.' + angular.lowercase(this.demat));
            }
            return '';
        }

        function _getInvoiceOrigGUID(attachmentItems) {
            return _.result(
                _.find(attachmentItems, function (a) {
                    return a.isOriginal();
                }),
                'attachmentId',
                ''
            );
        }

        /**
         * Indique si la facture a des pièces jointes
         */
        function hasAttachment() {
            return this.getNbrAttachments() > 0;
        }

        function getNbrAttachments() {
            return _.size(
                _.filter(this.attachmentItems, function (a) {
                    return !a.isMetaAttachment();
                })
            );
        }

        /**
         * Indique s'il existe un original pdf à télécharger
         */
        function hasOriginalFile() {
            return _.some(this.attachmentItems, function (a) {
                return a.isOriginal();
            });
        }

        /**
         * Libellé du nom du client avec le perimètre.
         */
        function displayLabelAndPerimeter() {
            if (this.perimeterName) {
                return this.buyerName + ' (' + this.perimeterName + ')';
            }
            return this.buyerName;
        }

        /**
         * Affiche le numéro de commande s'il existe sinon, le numéro de contrat
         */
        function getCommandOrContractRef() {
            if (this.orderRef) {
                return this.orderRef;
            }
            return this.contractRef;
        }

        function getFunctionalStatus(invoiceData) {
            let functionalStatus = null;
            if (invoiceData && invoiceData.technical) {
                functionalStatus = invoiceData.technical.status.find(st => st.code === 'FUNCTIONAL');
            }
            return functionalStatus;
        }
    }
})();
